import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import {
  CheckCircle2,
  Camera,
  Share2,
  ArrowRight,
  ChevronLeft,
  ChevronDown,
  Tag,
  Clock,
  Send,
} from 'lucide-react';
import { VenueCardPreview } from '../../components/venue/VenueCardPreview';
import { setVenueSetupReturnPath } from '../../utils/venueSetupBackNav';
import { useVenue } from '../../contexts/VenueContext';
import { useVenueSwitcher } from '../../hooks/useVenueSwitcher';
import { fetchSelectedVenueApplication } from '../../utils/venueAppApi';

// A day's operating-hours value counts as "set" once it holds a real
// range (e.g. "18:00-02:00") or an explicit "Closed" — an empty string
// means the owner never touched that day yet.
function hasAnyOperatingHours(hours) {
  if (!hours || typeof hours !== 'object') return false;
  return Object.values(hours).some((v) => typeof v === 'string' && v.trim().length > 0);
}

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const FONT = "'Inria Serif', serif";
const MIN_PHOTOS = 10;

/**
 * "Setup Mode" checklist — reached by tapping the "Finish setup to go
 * live" banner on the venue home page (`/venue/analytics`). The owner
 * already has full access to the real venue app (bottom nav, analytics,
 * scan, etc.) as soon as they submit the quick onboarding (Steps 1-2 +
 * Agreement); this sub-page tracks the remaining required items —
 * photos, venue vibe (categories/tags), operating hours, and payout —
 * plus optional extras (intro video, socials) they still owe before
 * the venue goes live on the guest map.
 */
export default function VenueSetupModeHome() {
  const navigate = useNavigate();
  // Setup Mode is per-venue: an owner with multiple venues can have one
  // in `live` status and another still in `setup_mode` (or with dropped
  // required items). Reading the currently-selected venue from the venue
  // switcher context — instead of defaulting to the latest-created
  // application — is what keeps this page's checklist honest. Without
  // this, tapping "Fix your setup" on Venue 1 would land here and show
  // Venue 2's checklist because the backend picks the most recently
  // created application when no `venue_app_id` is supplied.
  const { activeVenueAppId } = useVenue();
  // Merged switcher (own venues + any staff venue). We only render the
  // dropdown control when the owner has 2+ venues; single-venue owners
  // see a plain title so nothing looks tap-able for no reason.
  const { venues: switcherVenues, activeVenue: switcherActiveVenue, selectVenue, switching } = useVenueSwitcher();
  const [application, setApplication] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showVenuePicker, setShowVenuePicker] = useState(false);
  const venuePickerRef = useRef(null);

  // Close the inline venue picker on outside-tap so it feels like the
  // system's native menu behaviour.
  useEffect(() => {
    if (!showVenuePicker) return undefined;
    const handleOutside = (e) => {
      if (venuePickerRef.current && !venuePickerRef.current.contains(e.target)) {
        setShowVenuePicker(false);
      }
    };
    document.addEventListener('mousedown', handleOutside);
    document.addEventListener('touchstart', handleOutside);
    return () => {
      document.removeEventListener('mousedown', handleOutside);
      document.removeEventListener('touchstart', handleOutside);
    };
  }, [showVenuePicker]);

  // Belt-and-braces: stash the return path in sessionStorage so the
  // sub-pages' back arrows can find their way home even if React Router
  // state gets dropped (Capacitor cold-launch, history-replace redirect).
  const openSetupSubPage = (path) => {
    setVenueSetupReturnPath('/venue/setup-mode');
    navigate(path, { state: { from: '/venue/setup-mode' } });
  };

  const authHeaders = () => {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const app = await fetchSelectedVenueApplication(activeVenueAppId);
        if (cancelled) return;
        setApplication(app);
      } catch (err) {
        console.warn('Setup mode: failed to load application', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [activeVenueAppId]);

  const checklist = useMemo(() => {
    const photoCount = Array.isArray(application?.photo_urls) ? application.photo_urls.length : 0;
    const hasVideo = Boolean(application?.intro_video_url);
    const categoryCount = Array.isArray(application?.venue_categories) ? application.venue_categories.length : 0;
    const hasDescription = Boolean((application?.map_description || '').trim());
    const tagCount = Array.isArray(application?.venue_tags) ? application.venue_tags.length : 0;
    const vibeDone = categoryCount > 0 && hasDescription && tagCount > 0;
    const hoursSet = hasAnyOperatingHours(application?.operating_hours);
    // Required items first (photos, vibe, hours), optional (socials) LAST.
    // Payout is intentionally NOT part of this checklist — owners connect a
    // payout method only AFTER an Allure admin approves them (banner on
    // /venue/analytics handles that call-to-action separately).
    return [
      {
        key: 'photos',
        title: `Add ${MIN_PHOTOS}+ photos`,
        subtitle: (() => {
          if (photoCount >= MIN_PHOTOS && hasVideo) return `${photoCount} photos + intro video — looking great!`;
          if (photoCount >= MIN_PHOTOS) return `Done! Also add a short intro video (optional but highly recommended).`;
          if (photoCount > 0) return `${photoCount} / ${MIN_PHOTOS} photos uploaded. Intro video optional but recommended.`;
          return 'Also add a short intro video (optional, max 15 sec).';
        })(),
        done: photoCount >= MIN_PHOTOS,
        icon: Camera,
        cta: photoCount >= MIN_PHOTOS ? 'Manage media' : 'Add photos',
        onCta: () => openSetupSubPage('/venue/settings/photos'),
        required: true,
      },
      {
        key: 'vibe',
        title: "Pick your venue's vibe",
        subtitle: (() => {
          if (vibeDone) return 'Categories, description & tags set — this is what guests see on your card.';
          const missing = [];
          if (!categoryCount) missing.push('at least 1 category');
          if (!hasDescription) missing.push('a short description');
          if (!tagCount) missing.push('at least 1 tag');
          if (missing.length === 1) return `Still need ${missing[0]} — guests see this on your card.`;
          if (missing.length === 2) return `Add ${missing[0]} and ${missing[1]} — this is what guests see on your card.`;
          return 'Pick categories, write a short description and add at least 1 tag — guests see this on your card.';
        })(),
        done: vibeDone,
        icon: Tag,
        cta: vibeDone ? 'Edit vibe' : 'Set vibe',
        onCta: () => openSetupSubPage('/venue/settings/card'),
        required: true,
      },
      {
        key: 'hours',
        title: 'Set your operating hours',
        subtitle: hoursSet
          ? 'Weekly schedule set — closed days stay hidden from the map.'
          : 'Guests only see you when you\'re open.',
        done: hoursSet,
        icon: Clock,
        cta: hoursSet ? 'Edit hours' : 'Set hours',
        onCta: () => openSetupSubPage('/venue/settings/hours'),
        required: true,
      },
      {
        key: 'socials',
        title: 'Link your socials',
        subtitle: 'Optional. Instagram, TikTok and website links help guests trust you.',
        done: Boolean(
          application?.instagram
          || application?.website
          || application?.facebook
          || application?.tiktok,
        ),
        icon: Share2,
        cta: 'Add socials',
        onCta: () => openSetupSubPage('/venue/settings/social'),
        required: false,
      },
    ];
  }, [application, navigate]);

  const requiredDone = checklist.filter((item) => item.required && item.done).length;
  const requiredTotal = checklist.filter((item) => item.required).length;
  const progress = requiredTotal === 0 ? 0 : Math.round((requiredDone / requiredTotal) * 100);
  // A venue that's already approved / live is walking this page to fix
  // dropped compliance items (photos < 10, missing vibe/hours/tags) —
  // NOT to submit for admin review. The moment every required item is
  // ticked off the backend auto-republishes them, so the explicit
  // "Submit for review" button doesn't apply here.
  const isRepublishingLive = ['live', 'approved', 'pending', 'on_hold'].includes(application?.status || '');
  const readyToSubmit = !isRepublishingLive && requiredTotal > 0 && requiredDone === requiredTotal;
  const allRequiredComplete = requiredTotal > 0 && requiredDone === requiredTotal;
  const venueName = application?.restaurant_name || 'Your venue';

  const handleSubmitForReview = async () => {
    if (!readyToSubmit || submitting) return;
    setSubmitting(true);
    try {
      // Scope the submit to the venue we're actually reviewing. Without
      // `app_id` the backend would upsert into the caller's latest
      // application, which is the wrong venue when the owner has more
      // than one.
      const payload = { submitted: true };
      if (application?.id) payload.app_id = application.id;
      else if (activeVenueAppId) payload.app_id = activeVenueAppId;
      const res = await axios.post(
        `${API}/venue/applications`,
        payload,
        { headers: authHeaders() },
      );
      const updated = res?.data || null;
      if (updated) setApplication((prev) => ({ ...(prev || {}), ...updated }));
      // Refresh the local user object so the shell recognises the new
      // `venue_status` (pending / setup_mode) on the next render.
      try {
        const meRes = await axios.get(`${API}/auth/me`, { headers: authHeaders() });
        if (meRes?.data) localStorage.setItem('user', JSON.stringify(meRes.data));
      } catch { /* non-fatal — status will hydrate on next reload */ }
      toast.success('Sent to Allure for review — we\'ll be in touch shortly.');
      navigate('/venue/analytics', { replace: true });
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Couldn\'t submit right now — please try again.';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    // Render a real skeleton (back button + logo + title/body/checklist
    // placeholders) instead of a bare "Loading…" string. This keeps
    // BlankScreenRecovery's `looksBlank()` heuristic from mistaking the
    // slow first paint for a stuck app and dropping a full-screen
    // "Sign out and reset" overlay that blocks every click on the
    // healthy page underneath.
    return (
      <div
        className="app-shell premium-page"
        data-testid="venue-setup-mode-home"
        style={{ minHeight: '100dvh', background: 'var(--app-bg)', paddingBottom: 140 }}
      >
        <div style={{ padding: '20px 20px 8px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
          <button
            type="button"
            onClick={() => navigate('/venue/analytics')}
            data-testid="venue-setup-mode-back"
            aria-label="Back"
            style={{
              background: 'var(--surface)',
              border: '1px solid var(--card-border)',
              borderRadius: 12,
              width: 38,
              height: 38,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              flexShrink: 0,
            }}
          >
            <ChevronLeft size={20} color="var(--text)" strokeWidth={2.2} />
          </button>
          <div style={{ flex: 1, height: 32, borderRadius: 999, background: 'var(--accent-surface-soft)', opacity: 0.7 }} />
          <img src="/allure-logo.png" alt="Allure" style={{ width: 40, height: 36, objectFit: 'contain', flexShrink: 0 }} />
        </div>
        <div style={{ padding: '18px 20px 0' }}>
          <div style={{ width: '75%', height: 26, borderRadius: 8, background: 'var(--accent-surface-soft)' }} />
          <div style={{ marginTop: 12, width: '90%', height: 15, borderRadius: 6, background: 'var(--accent-surface-soft)', opacity: 0.7 }} />
          <div style={{ marginTop: 20, height: 78, borderRadius: 18, background: 'var(--surface)', border: '1px solid var(--card-border)', boxShadow: 'var(--premium-shadow-nested)' }} />
          <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
            {[0, 1, 2, 3].map((i) => (
              <div key={i} style={{ height: 76, borderRadius: 16, background: 'var(--surface)', border: '1px solid var(--card-border)', boxShadow: 'var(--premium-shadow-nested)' }} />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="app-shell premium-page"
      data-testid="venue-setup-mode-home"
      style={{
        minHeight: '100dvh',
        background: 'var(--app-bg)',
        paddingBottom: 140,
      }}
    >
      {/* Header — back arrow + inline venue switcher (multi-venue owners
          need to see which venue's checklist they're editing) + logo. */}
      <div style={{ padding: '20px 20px 8px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
        <button
          type="button"
          onClick={() => navigate('/venue/analytics')}
          data-testid="venue-setup-mode-back"
          aria-label="Back"
          style={{
            background: 'var(--surface)',
            border: '1px solid var(--card-border)',
            borderRadius: 12,
            width: 38,
            height: 38,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            flexShrink: 0,
          }}
        >
          <ChevronLeft size={20} color="var(--text)" strokeWidth={2.2} />
        </button>

        {/* Which venue am I editing? — chip is a plain label for
            single-venue owners; opens an inline switcher when the owner
            has 2+ venues so they can hop between checklists without
            leaving the page. */}
        <div style={{ position: 'relative', flex: 1, minWidth: 0, display: 'flex', justifyContent: 'center' }} ref={venuePickerRef}>
          {switcherVenues.length > 1 ? (
            <button
              type="button"
              onClick={() => setShowVenuePicker((v) => !v)}
              disabled={switching}
              data-testid="venue-setup-mode-venue-switcher"
              aria-haspopup="listbox"
              aria-expanded={showVenuePicker}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                maxWidth: '100%',
                padding: '7px 12px',
                borderRadius: 999,
                background: 'var(--accent-surface-soft)',
                border: '1px solid var(--card-border)',
                color: 'var(--accent-purple)',
                fontFamily: FONT,
                fontSize: 12.5,
                fontWeight: 700,
                cursor: switching ? 'not-allowed' : 'pointer',
                opacity: switching ? 0.6 : 1,
              }}
            >
              <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', opacity: 0.75 }}>
                Editing
              </span>
              <span
                data-testid="venue-setup-mode-venue-switcher-label"
                style={{ maxWidth: 160, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
              >
                {venueName}
              </span>
              <ChevronDown size={14} strokeWidth={2.4} style={{ flexShrink: 0 }} />
            </button>
          ) : (
            <span
              data-testid="venue-setup-mode-venue-label"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                maxWidth: '100%',
                padding: '7px 12px',
                borderRadius: 999,
                background: 'var(--accent-surface-soft)',
                color: 'var(--accent-purple)',
                fontFamily: FONT,
                fontSize: 12.5,
                fontWeight: 700,
              }}
            >
              <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', opacity: 0.75 }}>
                Editing
              </span>
              <span style={{ maxWidth: 180, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {venueName}
              </span>
            </span>
          )}
          {showVenuePicker && switcherVenues.length > 1 ? (
            <div
              data-testid="venue-setup-mode-venue-switcher-dropdown"
              role="listbox"
              style={{
                position: 'absolute',
                top: 'calc(100% + 8px)',
                left: '50%',
                transform: 'translateX(-50%)',
                background: 'var(--surface)',
                borderRadius: 14,
                padding: 6,
                boxShadow: 'var(--shadow-2)',
                border: '1px solid var(--card-border)',
                minWidth: 220,
                maxHeight: 280,
                overflowY: 'auto',
                zIndex: 200,
              }}
            >
              {switcherVenues.map((v) => {
                const isActive = v.id === switcherActiveVenue?.id;
                const name = (v.map_name || v.restaurant_name || 'Venue').trim() || 'Venue';
                return (
                  <button
                    key={v.id}
                    type="button"
                    onClick={() => { selectVenue(v); setShowVenuePicker(false); }}
                    disabled={switching}
                    data-testid={`venue-setup-mode-venue-switcher-item-${v.id}`}
                    role="option"
                    aria-selected={isActive}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 10,
                      width: '100%',
                      padding: '8px 10px',
                      background: isActive ? 'var(--accent-surface-soft)' : 'transparent',
                      border: 'none',
                      borderRadius: 10,
                      cursor: switching ? 'not-allowed' : 'pointer',
                      fontFamily: FONT,
                      fontSize: 13.5,
                      fontWeight: 700,
                      color: 'var(--text)',
                      textAlign: 'left',
                    }}
                  >
                    <span style={{ minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {name}
                    </span>
                    {isActive ? <CheckCircle2 size={16} color="var(--accent-purple)" strokeWidth={2.4} /> : null}
                  </button>
                );
              })}
            </div>
          ) : null}
        </div>

        <img src="/allure-logo.png" alt="Allure" style={{ width: 40, height: 36, objectFit: 'contain', flexShrink: 0 }} />
      </div>

      {/* Title + progress */}
      <div style={{ padding: '0 20px' }}>
        <motion.h1
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
          data-testid="venue-setup-mode-title"
          style={{
            margin: '18px 0 6px',
            fontFamily: FONT,
            fontSize: 26,
            lineHeight: 1.2,
            fontWeight: 700,
            color: 'var(--text)',
            letterSpacing: '-0.01em',
          }}
        >
          {isRepublishingLive ? `Reappear on the map — ${venueName}` : `Finish setting up ${venueName}`}
        </motion.h1>
        <p
          style={{
            margin: 0,
            fontFamily: FONT,
            fontSize: 15,
            lineHeight: 1.5,
            color: 'var(--text-2)',
            fontWeight: 500,
          }}
        >
          {isRepublishingLive
            ? (allRequiredComplete
                ? 'You\'re back on the guest map — no need to resubmit.'
                : 'Your venue is temporarily hidden. Fix the items below and it comes right back on the guest map.')
            : 'Complete these last steps and you\u2019ll be live on the guest map.'}
        </p>

        <div
          style={{
            marginTop: 20,
            padding: 16,
            borderRadius: 18,
            background: 'var(--surface)',
            border: '1px solid var(--card-border)',
            boxShadow: 'var(--premium-shadow-nested)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
            <p style={{ margin: 0, fontFamily: FONT, fontSize: 14, fontWeight: 700, color: 'var(--text)' }}>
              {requiredDone === requiredTotal ? 'You\'re ready to go live 🎉' : `Setup progress · ${requiredDone}/${requiredTotal} required`}
            </p>
            <span
              data-testid="venue-setup-mode-progress-percent"
              style={{
                fontFamily: FONT,
                fontSize: 13,
                fontWeight: 700,
                color: 'var(--accent-purple)',
              }}
            >
              {progress}%
            </span>
          </div>
          <div
            style={{
              width: '100%',
              height: 8,
              borderRadius: 999,
              background: 'var(--accent-surface-soft)',
              overflow: 'hidden',
            }}
          >
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              style={{
                height: '100%',
                background: 'var(--accent-purple)',
              }}
            />
          </div>
        </div>
      </div>

      {/* Checklist */}
      <div style={{ padding: '20px 20px 0' }}>
        <p
          style={{
            margin: '0 0 10px 4px',
            fontFamily: FONT,
            fontSize: 13,
            fontWeight: 700,
            color: 'var(--accent-purple)',
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
          }}
        >
          Your checklist
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {checklist.map((item, idx) => (
            <motion.div
              key={item.key}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: idx * 0.06, ease: [0.22, 1, 0.36, 1] }}
              data-testid={`venue-setup-mode-checklist-${item.key}`}
              style={{
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                padding: '14px 16px',
                paddingTop: item.required ? 24 : 14,
                borderRadius: 16,
                background: 'var(--surface)',
                border: item.done ? '1px solid rgba(31, 142, 42, 0.20)' : '1px solid var(--card-border)',
                boxShadow: 'var(--premium-shadow-nested)',
              }}
            >
              {item.required ? (
                <span
                  data-testid={`venue-setup-mode-required-pill-${item.key}`}
                  style={{
                    position: 'absolute',
                    top: 8,
                    right: 10,
                    fontFamily: FONT,
                    fontSize: 9.5,
                    fontWeight: 700,
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    color: 'var(--accent-purple)',
                    background: 'var(--accent-surface-soft)',
                    padding: '3px 8px',
                    borderRadius: 999,
                    lineHeight: 1,
                  }}
                >
                  Required
                </span>
              ) : null}
              <span
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: 12,
                  background: item.done ? 'var(--status-success-surface)' : 'var(--accent-surface-soft)',
                  color: item.done ? 'var(--status-success-text)' : 'var(--accent-purple)',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                {item.done ? <CheckCircle2 size={20} strokeWidth={2.2} /> : <item.icon size={18} strokeWidth={2.2} />}
              </span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{ margin: 0, fontFamily: FONT, fontSize: 15, fontWeight: 700, color: 'var(--text)' }}>
                  {item.title}
                </p>
                <p style={{ margin: '2px 0 0', fontFamily: FONT, fontSize: 12.5, color: 'var(--text-3)', fontWeight: 500 }}>
                  {item.subtitle}
                </p>
              </div>
              <button
                type="button"
                onClick={item.onCta}
                data-testid={`venue-setup-mode-cta-${item.key}`}
                style={{
                  padding: '8px 12px',
                  borderRadius: 12,
                  border: 'none',
                  background: item.done ? 'var(--accent-surface-soft)' : 'var(--accent-purple)',
                  color: item.done ? 'var(--accent-purple)' : '#FFFFFF',
                  fontFamily: FONT,
                  fontSize: 12.5,
                  fontWeight: 700,
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 4,
                  flexShrink: 0,
                  boxShadow: item.done ? 'none' : 'var(--premium-shadow-main)',
                }}
              >
                {item.cta}
                <ArrowRight size={13} strokeWidth={2.4} />
              </button>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Submit for review CTA — enabled only once every required item is
          ticked off. Placed between the checklist and the guest card
          preview so it reads as the payoff for finishing the checklist.
          Live/approved venues fixing dropped compliance don't need to
          resubmit (auto-republish), so we show a passive info tile
          instead of the primary button. */}
      <div style={{ padding: '20px 20px 0' }}>
        {isRepublishingLive ? (
          <div
            data-testid="venue-setup-mode-republish-hint"
            style={{
              padding: '14px 16px',
              borderRadius: 18,
              background: allRequiredComplete ? 'var(--status-success-surface)' : 'var(--accent-surface-soft)',
              border: allRequiredComplete ? '1px solid rgba(31, 142, 42, 0.22)' : '1px solid var(--card-border)',
              boxShadow: 'var(--premium-shadow-nested)',
              display: 'flex',
              alignItems: 'flex-start',
              gap: 10,
            }}
          >
            <span
              style={{
                width: 32,
                height: 32,
                borderRadius: 10,
                background: allRequiredComplete ? 'var(--status-success-surface)' : 'var(--accent-surface)',
                color: allRequiredComplete ? 'var(--status-success-text)' : 'var(--accent-purple)',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              {allRequiredComplete ? <CheckCircle2 size={16} strokeWidth={2.4} /> : <Send size={15} strokeWidth={2.2} />}
            </span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <p style={{ margin: 0, fontFamily: FONT, fontSize: 14, fontWeight: 700, color: allRequiredComplete ? 'var(--status-success-text)' : 'var(--text)' }}>
                {allRequiredComplete ? 'You\'re back on the map' : 'Auto-republishes when everything\'s ticked off'}
              </p>
              <p style={{ margin: '4px 0 0', fontFamily: FONT, fontSize: 12.5, color: allRequiredComplete ? 'var(--status-success-text)' : 'var(--text-3)', lineHeight: 1.5, fontWeight: 500, opacity: allRequiredComplete ? 0.9 : 1 }}>
                {allRequiredComplete
                  ? 'Guests can see your venue again — no need to resubmit.'
                  : `Finish ${requiredTotal - requiredDone} more required step${requiredTotal - requiredDone === 1 ? '' : 's'} above and your venue reappears on the guest map automatically.`}
              </p>
            </div>
          </div>
        ) : (
          <>
            <motion.button
              type="button"
              whileTap={readyToSubmit && !submitting ? { scale: 0.97 } : undefined}
              onClick={handleSubmitForReview}
              disabled={!readyToSubmit || submitting}
              data-testid="venue-setup-mode-submit-for-review"
              style={{
                width: '100%',
                padding: '15px 20px',
                borderRadius: 18,
                border: 'none',
                background: readyToSubmit ? 'var(--accent-purple)' : 'var(--accent-surface)',
                color: readyToSubmit ? '#FFFFFF' : 'var(--text-3)',
                fontFamily: FONT,
                fontSize: 16,
                fontWeight: 700,
                cursor: readyToSubmit && !submitting ? 'pointer' : 'default',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
                boxShadow: readyToSubmit ? 'var(--premium-shadow-main)' : 'none',
                transition: 'background 200ms ease, box-shadow 200ms ease, color 200ms ease',
              }}
            >
              <Send size={16} strokeWidth={2.2} />
              {submitting
                ? 'Submitting…'
                : readyToSubmit
                  ? 'Submit for review'
                  : `Finish ${requiredTotal - requiredDone} more required step${requiredTotal - requiredDone === 1 ? '' : 's'}`}
            </motion.button>
            <p
              data-testid="venue-setup-mode-submit-for-review-hint"
              style={{
                margin: '8px 4px 0',
                fontFamily: FONT,
                fontSize: 12.5,
                color: 'var(--text-3)',
                fontWeight: 500,
                lineHeight: 1.5,
              }}
            >
              {readyToSubmit
                ? 'Allure will review your venue and reach out by email once it\'s approved.'
                : 'This button unlocks the moment every required item above is ticked off.'}
            </p>
          </>
        )}
      </div>

      {/* Live card preview — exactly how guests will see this venue */}
      <div style={{ padding: '28px 20px 0' }}>
        <p
          style={{
            margin: '0 0 10px 4px',
            fontFamily: FONT,
            fontSize: 13,
            fontWeight: 700,
            color: 'var(--accent-purple)',
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
          }}
        >
          How guests will see you
        </p>
        <VenueCardPreview
          testId="venue-setup-mode-card-preview"
          photoUrl={Array.isArray(application?.photo_urls) ? application.photo_urls[0] : ''}
          name={application?.restaurant_name}
          categories={application?.venue_categories}
          description={application?.map_description}
        />
        <p
          style={{
            margin: '10px 4px 0',
            fontFamily: FONT,
            fontSize: 12.5,
            color: 'var(--text-3)',
            fontWeight: 500,
            lineHeight: 1.5,
          }}
        >
          This updates live as you add photos above. Want to tweak the categories or subtitle? Head to{' '}
          <button
            type="button"
            onClick={() => { setVenueSetupReturnPath('/venue/setup-mode'); navigate('/venue/settings/card', { state: { from: '/venue/setup-mode' } }); }}
            data-testid="venue-setup-mode-edit-card-link"
            style={{ background: 'none', border: 'none', padding: 0, color: 'var(--accent-purple)', fontFamily: FONT, fontSize: 12.5, fontWeight: 700, cursor: 'pointer', textDecoration: 'underline' }}
          >
            Venue Card settings
          </button>
          .
        </p>
      </div>
    </div>
  );
}
